import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { scheduleSteps } from './emailAdminModel';

// The countdown's timeline: a horizontal line that ends at "event starts", one mark per day at its
// distance. The scale is fixed (60 days before … the event start), not the span of the days present,
// so a mark always means the same thing when a day is added or removed. The line is decorative - the
// day list below it carries the actions and its text is what a screen reader reads.
//
// Overlap: labels sit in two lanes below the line, alternating left to right. Two marks next to each
// other are always in different lanes, so the same lane is at least two days apart; at 900 px that is
// about 25 px and a one- or two-digit label is ~10-16 px. The track keeps a 1.4rem margin so the
// labels of 60 (left edge) and 1 (right edge) do not run off. Under 480 px the line is hidden and the
// list below alone shows the days (the owner allowed a list at 390 px).
const MAX_DAY = 60;
const LANE_OFFSET = [1.95, 3.2]; // rem, below the line
const LINE_TOP = 1.45; // rem
const LANE_STEM = [0.5, 1.75]; // rem, from the line to the label

const Wrap = styled.div`
  margin: .5rem 0 1rem;

  @media (max-width: 480px) {
    display: none;
  }
`;

const Scale = styled.div`
  display: flex;
  justify-content: space-between;
  gap: .5rem;
  font-size: .7rem;
  color: #64748b;
  margin-bottom: .2rem;
`;

const Track = styled.div`
  position: relative;
  height: 4.6rem;
  margin: 0 1.4rem;
`;

const Line = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${LINE_TOP}rem;
  border-top: 2px solid #cbd5e1;
`;

const End = styled.div`
  position: absolute;
  right: 0;
  top: ${LINE_TOP - 0.3}rem;
  width: 2px;
  height: .75rem;
  background: #111D4E;
`;

const Mark = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
`;

const Dot = styled.span`
  position: absolute;
  left: -4px;
  top: ${LINE_TOP - 0.2}rem;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #47ABDB;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #94a3b8;
`;

const Stem = styled.span`
  position: absolute;
  left: -.5px;
  top: ${LINE_TOP}rem;
  width: 1px;
  height: ${(p) => LANE_STEM[p.$lane]}rem;
  background: #cbd5e1;
`;

const Label = styled.span`
  position: absolute;
  left: 0;
  top: ${(p) => LANE_OFFSET[p.$lane]}rem;
  transform: translateX(-50%);
  font-size: .72rem;
  font-weight: 700;
  color: #334155;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  background: #fff;
  padding: 0 .15rem;
`;

const ScheduleTimeline = ({ schedule }) => {
  const intl = useIntl();
  const days = scheduleSteps(schedule);

  return (
    <Wrap aria-hidden="true">
      <Scale>
        <span>{intl.formatMessage({ id: 'emailAdmin.schedules.timeline.scaleStart', defaultMessage: '60 days before' })}</span>
        <span>{intl.formatMessage({ id: 'emailAdmin.schedules.timeline.eventStarts', defaultMessage: 'Event starts' })}</span>
      </Scale>
      <Track>
        <Line />
        <End />
        {days.map((step, index) => {
          const left = ((MAX_DAY - step.daysBeforeStart) / MAX_DAY) * 100;
          const lane = index % 2;
          return (
            <Mark key={step.id} style={{ left: `${left}%` }}>
              <Dot />
              <Stem $lane={lane} />
              <Label $lane={lane}>{step.daysBeforeStart}</Label>
            </Mark>
          );
        })}
      </Track>
    </Wrap>
  );
};

export default ScheduleTimeline;
